import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PageHeader,
  DashboardCard,
  FilterBar,
  StatusBadge,
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@zafroi/ui-kit';
import { AlertTriangle } from 'lucide-react';
import { useOutstandings, useCustomerNameMap } from '@/hooks/useKosh';
import { QueryStateGuard } from '@/components/common/QueryStateGuard';
import { outstandingStatusVariants } from '@/config/kosh-status';
import { formatMoney, shortDate } from '@/lib/format';

export function ReceivablesPage() {
  const navigate = useNavigate();
  const q = useOutstandings();
  const nameMap = useCustomerNameMap();
  const [search, setSearch] = useState('');
  const [openOnly, setOpenOnly] = useState(true);

  const items = q.data ?? [];
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return items.filter((o) => {
      if (openOnly && (o.status === 'SETTLED' || o.status === 'WRITTEN_OFF')) return false;
      if (!s) return true;
      const name = nameMap.get(o.customerId) ?? '';
      return name.toLowerCase().includes(s) || (o.invoice?.invoiceNumber ?? '').toLowerCase().includes(s);
    });
  }, [items, search, openOnly, nameMap]);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Financial"
        title="Receivables"
        description="Outstanding balances, aging and disputes."
      />
      <DashboardCard flushBody>
        <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
          <FilterBar searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search receivables…" />
          <label className="ml-auto flex items-center gap-2 text-[12px] text-muted-foreground">
            <input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} />
            Open only
          </label>
        </div>
        <QueryStateGuard
          data={filtered}
          isLoading={q.isLoading}
          isError={q.isError}
          emptyTitle="No outstanding balances"
          emptyDescription="Issued invoices that aren't fully settled appear here."
        >
          {(rows) => (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead className="text-right">Original</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((o) => (
                  <TableRow
                    key={o.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/receivables/${o.id}`)}
                  >
                    <TableCell className="font-mono text-[12px]">
                      {o.invoice?.invoiceNumber ?? o.invoiceId.slice(0, 8)}
                    </TableCell>
                    <TableCell className="font-medium">
                      {nameMap.get(o.customerId) ?? o.customerId.slice(0, 8)}
                      {o.inDispute && (
                        <AlertTriangle className="ml-1 inline h-3.5 w-3.5 text-[hsl(38_85%_45%)]" />
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatMoney(o.originalAmount, o.invoice?.currency ?? 'INR')}
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums">
                      {formatMoney(o.balanceAmount, o.invoice?.currency ?? 'INR')}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{shortDate(o.dueDate)}</TableCell>
                    <TableCell>
                      <StatusBadge status={o.status} variants={outstandingStatusVariants} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </QueryStateGuard>
      </DashboardCard>
    </div>
  );
}
